import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { PageLayout, PageHeader } from "../components/layout";
import { Button, StatePanel, Toggle } from "../components/ui";
import NotificationItem from "./NotificationItem";
import { groupNotifications } from "./notificationPresentation";
import {
  markAllNotificationsRead,
  markNotificationRead,
  useNotificationList,
  useRefreshNotifications,
} from "./useNotificationCenter";
import "./rc-notification-item.scss";
import "./rc-notification-center.scss";
import { HOME_PATH } from "../core/config/dashboard.js";

const CATEGORY_FILTERS = [
  { value: null, label: "All" },
  { value: "pulse", label: "Pulse" },
  { value: "alert", label: "Alert" },
  { value: "notification", label: "Notification" },
];

/**
 * /notifications — the notification center's "view all" page.
 *
 * Rows are GROUPED BY KIND (Leads, Payments, Buying opportunities…) and newest
 * first inside each group. Before grouping, a store with an active buy alert
 * saw thirty near-identical rows and had to scroll past all of them to find
 * the payment that mattered.
 */
const NotificationsPage = () => {
  const history = useHistory();
  const [category, setCategory] = useState(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const refreshNotifications = useRefreshNotifications();

  const filters = useMemo(() => ({ category, unreadOnly }), [category, unreadOnly]);
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useNotificationList(filters);

  const items = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page?.data ?? []),
    [data]
  );
  const groups = useMemo(() => groupNotifications(items), [items]);

  const openNotification = (item) => {
    if (!item.isRead) {
      markNotificationRead(item.id).then(refreshNotifications).catch(() => {});
    }
    if (item.url) history.push(item.url);
  };

  const onMarkAllRead = () => {
    markAllNotificationsRead().then(refreshNotifications).catch(() => {});
  };

  return (
    <PageLayout className="rc-notification-center">
      <PageHeader
        title="Notifications"
        description="Pulses, alerts and updates from across your store."
        backTo={HOME_PATH}
        actions={[
          {
            label: "Mark all as read",
            onClick: onMarkAllRead,
            variant: "outline",
            size: "lg",
          },
        ]}
      />

      <div className="rc-notification-center__filters">
        <div
          className="rc-notification-center__pills"
          role="group"
          aria-label="Filter by category"
        >
          {CATEGORY_FILTERS.map((f) => (
            <Button
              key={f.label}
              variant="tab"
              size="md"
              active={category === f.value}
              aria-pressed={category === f.value}
              onClick={() => setCategory(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <Toggle label="Unread only" checked={unreadOnly} onChange={setUnreadOnly} size="md" />
      </div>

      {isLoading ? (
        <StatePanel state="loading" loadingShape="row" loadingCount={4} />
      ) : isError && items.length === 0 ? (
        // A failed fetch must never masquerade as an empty inbox.
        <StatePanel state="error" onRetry={() => refetch()} />
      ) : items.length === 0 ? (
        <StatePanel
          state={category || unreadOnly ? "no-results" : "empty"}
          icon="Inbox"
          title={unreadOnly ? "Nothing unread — you’re all caught up." : "No notifications yet."}
          description={null}
        />
      ) : (
        <div className="rc-notification-center__groups">
          {groups.map((group) => (
            <section
              key={group.key}
              className="rc-notification-center__group"
              aria-labelledby={`rc-note-group-${group.key}`}
            >
              <div className="rc-note-group">
                <h2 className="rc-note-group__label" id={`rc-note-group-${group.key}`}>
                  {group.label}
                </h2>
                {group.unread > 0 && (
                  <span className="rc-note-group__count">
                    {group.unread}
                    <span className="rc-notification-center__sr"> unread</span>
                  </span>
                )}
                <span className="rc-note-group__rule" />
              </div>
              <div className="rc-notification-center__list">
                {group.items.map((item) => (
                  <NotificationItem key={item.id} item={item} onOpen={openNotification} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {hasNextPage && (
        <div className="rc-notification-center__more">
          <Button
            variant="outline"
            size="md"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </PageLayout>
  );
};

export default NotificationsPage;
